import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from '../../translations';

/**
 * KARTKA A4 dla podglądów dokumentów (konstruktor umowy i konstruktor oferty).
 *
 * Do tej pory podglądy były „białym prostokątem o szerokości 760 px". Wyglądały jak kartka,
 * ale nią nie były: tekst łamał się w innym miejscu niż w PDF, marginesy były przypadkowe,
 * a o tym, że dokument nie mieści się na jednej stronie, klient dowiadywał się dopiero po
 * wygenerowaniu pliku.
 *
 * Tutaj arkusz ma PRAWDZIWE wymiary A4 i PRAWDZIWE marginesy druku, a do szerokości kolumny
 * jest tylko przeskalowany (`transform: scale`). To istotna różnica: skalowanie nie zmienia
 * układu, więc wiersze łamią się dokładnie tam, gdzie na papierze — inaczej niż przy zwężaniu
 * kartki, które przelewa tekst do następnej linii i pokazuje dokument, którego nie będzie.
 *
 * DLACZEGO 96/25.4: Chrome (a więc i `page.pdf` w puppeteerze, który generuje nasze dokumenty)
 * liczy jednostkę `mm` jako 96 dpi. Ta sama stała po obu stronach = ta sama szerokość łamania
 * tekstu w podglądzie i w pliku.
 *
 * DLACZEGO TRZY POZIOMY ZAGNIEŻDŻENIA (Scroller → Viewport → Sheet): `transform: scale` NIE
 * zmienia rozmiaru w układzie — arkusz zajmuje 210 mm niezależnie od powiększenia. Bez
 * `Viewport` o szerokości równej faktycznie widocznej (przyciętego `overflow: hidden`)
 * pusty prawy margines rozpychałby kolumnę i poziomy pasek pojawiałby się nawet przy
 * dopasowaniu do szerokości.
 *
 * PODZIAŁ NA STRONY JEST ORIENTACYJNY. Arkusz jest jednym ciągłym blokiem, więc linie stron
 * wyliczamy z wysokości treści (`ceil(wysokość / obszar użytkowy strony)`). Prawdziwy render
 * przesuwa złamania, żeby nie przeciąć wiersza tabeli ani bloku podpisów
 * (`page-break-inside: avoid` w `Login/backend/lib/contractHtml.js`) — dokładny podział
 * pokazuje dopiero wygenerowany PDF. Dlatego linie są przerywane i podpisane, a nie udają
 * osobnych kartek.
 */

/**
 * „1 strona" / „3 strony" / „5 stron". Odmiana przez liczbę, bo oba konstruktory wypisują
 * tę liczbę pod kartką, a `${n} strony` daje „5 strony".
 */
export function pageCountLabel(n) {
  const count = Math.max(1, Math.round(Number(n) || 1));
  if (count === 1) return '1 strona';
  const last = count % 10;
  const lastTwo = count % 100;
  const few = last >= 2 && last <= 4 && !(lastTwo >= 12 && lastTwo <= 14);
  return `${count} ${few ? 'strony' : 'stron'}`;
}

const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
const MM_TO_PX = 96 / 25.4;

const Bar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--sp-1);
  margin-bottom: var(--sp-2);
`;

const ZoomButton = styled.button`
  padding: 3px var(--sp-3);
  border-radius: var(--r-pill);
  border: 1px solid ${(p) => (p.$active ? 'var(--primary)' : 'var(--input-border)')};
  background: ${(p) => (p.$active ? 'var(--info-bg)' : 'var(--card-bg)')};
  color: ${(p) => (p.$active ? 'var(--info)' : 'var(--text-muted)')};
  font-family: inherit;
  font-size: var(--fs-xs);
  font-weight: 600;
  cursor: pointer;
  transition: border-color var(--dur) var(--ease), background var(--dur) var(--ease);

  &:hover { border-color: var(--primary); }
`;

/* Poziome przewijanie TYLKO w tej warstwie i tylko wtedy, gdy kartka jest szersza niż kolumna
   (powiększenie 100%). `overflow-y: hidden` jest tu obowiązkowe: CSS nie pozwala mieć jednej
   osi `auto`, a drugiej `visible` — bez tego powstałby drugi, pionowy pasek przewijania
   na tej samej treści, co panel podglądu. */
const Scroller = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
`;

const Viewport = styled.div`
  position: relative;
  margin: 0 auto;
  overflow: hidden;
`;

const Sheet = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: ${A4_WIDTH_MM}mm;
  box-sizing: border-box;
  transform-origin: top left;
  background: #fff;
  /* Kartka ma być biała w KAŻDYM motywie — to wydruk, nie element panelu. */
  color: #1a1a1a;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.18), 0 10px 30px rgba(15, 23, 42, 0.10);
`;

/* `flow-root` odcina zlewanie się marginesów: bez tego margines górny pierwszego elementu
   treści wypychałby CAŁY blok w dół zamiast odsunąć się od górnego marginesu kartki. */
const Content = styled.div`
  display: flow-root;
`;

const PageBreak = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  border-top: 1px dashed #b4b4bd;
  pointer-events: none;
`;

const PageBreakLabel = styled.span`
  position: absolute;
  right: 8px;
  top: 4px;
  padding: 1px 8px;
  border-radius: 999px;
  background: #f2f2f2;
  border: 1px solid #dcdcdc;
  color: #5b5b5b;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.5;
  white-space: nowrap;
`;

/* Stopka strony z PDF (`buildPdfFooterTemplate` w Login/backend/lib/contractHtml.js): nazwa firmy
   po lewej, „Strona X z Y” po prawej, 7,5 pt, szara, w dolnym marginesie. Chrome stawia stopkę
   0,4 cm nad dolną krawędzią kartki, z bocznym odstępem 15 mm jak w szablonie. Kolory wpisane
   wprost, bo kartka ma wyglądać jak wydruk w każdym motywie. */
const PageFooter = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 4mm;
  padding: 0 15mm;
  display: flex;
  justify-content: space-between;
  gap: 8mm;
  font-family: 'Segoe UI', Arial, sans-serif;
  font-size: 7.5pt;
  line-height: 1.2;
  color: #6b7280;
  pointer-events: none;
`;

/**
 * @param {number} marginTopMm     górny margines druku (MUSI odpowiadać wywołaniu page.pdf)
 * @param {number} marginSideMm    marginesy boczne
 * @param {number} marginBottomMm  dolny margines druku
 * @param {function} onPagesChange liczba stron (do podpisu pod kartką po stronie wywołującego)
 * @param {string} [pageFooter]    tekst lewej części stopki strony PDF (zwykle nazwa firmy); bez
 *                                 propa stopki nie ma. Kartka jest jednym blokiem bez marginesów
 *                                 między stronami, więc stopka stoi pod ostatnią stroną, jak
 *                                 w dolnym marginesie wydruku (test odbiorczy 11.10.2026).
 */
export default function PaperSheet({
  marginTopMm = 16,
  marginSideMm = 15,
  marginBottomMm = 16,
  onPagesChange,
  pageFooter,
  children,
}) {
  const scrollerRef = useRef(null);
  const contentRef = useRef(null);
  const [availableWidth, setAvailableWidth] = useState(0);
  const [contentHeight, setContentHeight] = useState(0);
  const { t } = useTranslation();
  // 'fit' = cała szerokość kartki widoczna; 1 = skala 1:1 z wydrukiem (czytelne pismo,
  // kartka szersza od kolumny przewija się w poziomie).
  const [zoom, setZoom] = useState('fit');

  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    const read = () => setAvailableWidth(el.clientWidth);
    read();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Wysokość treści → liczba stron. ResizeObserver, a nie pomiar przy renderze, bo treść
  // zmienia wysokość także BEZ przerysowania Reacta (np. gdy doczyta się logo firmy).
  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return undefined;
    const read = () => setContentHeight(el.scrollHeight);
    read();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const sheetWidthPx = A4_WIDTH_MM * MM_TO_PX;
  const fitScale = availableWidth > 0 ? Math.min(1, availableWidth / sheetWidthPx) : 1;
  // Kartki NIE powiększamy ponad 100% — dokument ma wyglądać jak wydruk, a nie jak plakat.
  const scale = zoom === 'fit' ? fitScale : Math.min(1, zoom);

  const usablePx = (A4_HEIGHT_MM - marginTopMm - marginBottomMm) * MM_TO_PX;
  const pages = Math.max(1, Math.ceil((contentHeight || 1) / usablePx));
  const sheetHeightPx = (marginTopMm + marginBottomMm) * MM_TO_PX + pages * usablePx;

  useEffect(() => {
    if (onPagesChange) onPagesChange(pages);
  }, [pages, onPagesChange]);

  // Przełącznik ma sens dopiero wtedy, gdy kartka faktycznie się nie mieści.
  const canZoom = fitScale < 0.995;

  return (
    <div>
      {canZoom && (
        <Bar role="group" aria-label={t('paperSheet.zoomGroup')}>
          <ZoomButton type="button" $active={zoom === 'fit'} onClick={() => setZoom('fit')}>
            {t('paperSheet.fitPage')}
          </ZoomButton>
          <ZoomButton type="button" $active={zoom !== 'fit'} onClick={() => setZoom(1)}>
            100%
          </ZoomButton>
        </Bar>
      )}

      <Scroller ref={scrollerRef}>
        <Viewport style={{ width: sheetWidthPx * scale, height: sheetHeightPx * scale }}>
          <Sheet
            style={{
              transform: `scale(${scale})`,
              height: sheetHeightPx,
              padding: `${marginTopMm}mm ${marginSideMm}mm ${marginBottomMm}mm`,
            }}
          >
            <Content ref={contentRef}>{children}</Content>

            {Array.from({ length: pages - 1 }, (_, i) => (
              <PageBreak
                key={i}
                style={{ top: marginTopMm * MM_TO_PX + (i + 1) * usablePx }}
                aria-hidden="true"
              >
                <PageBreakLabel>Strona {i + 2}</PageBreakLabel>
              </PageBreak>
            ))}

            {pageFooter !== undefined && pageFooter !== null && (
              <PageFooter data-cy="paper-page-footer" aria-hidden="true">
                <span>{pageFooter}</span>
                <span>Strona {pages} z {pages}</span>
              </PageFooter>
            )}
          </Sheet>
        </Viewport>
      </Scroller>
    </div>
  );
}
